import { Map as ImmutableMap, List as ImmutableList } from 'immutable';

import { blockAccountSuccess, muteAccountSuccess } from 'mastodon/actions/accounts';
import { blockDomainSuccess } from 'mastodon/actions/domain_blocks';

import {
  CONVERSATIONS_MOUNT,
  CONVERSATIONS_UNMOUNT,
  CONVERSATIONS_FETCH_REQUEST,
  CONVERSATIONS_FETCH_SUCCESS,
  CONVERSATIONS_FETCH_FAIL,
  CONVERSATIONS_UPDATE,
  CONVERSATIONS_READ,
  CONVERSATIONS_UNREAD_COUNT,
  CONVERSATIONS_DELETE_SUCCESS,
} from '../actions/conversations';
import { compareId } from '../compare_id';

const initialState = ImmutableMap({
  items: ImmutableList(),
  isLoading: false,
  hasMore: true,
  mounted: false,
  unreadCount: 0,
});

const clampCount = count => Math.max(0, count);

const conversationToMap = item => ImmutableMap({
  id: item.id,
  unread: item.unread,
  accounts: ImmutableList(item.accounts.map(a => a.id)),
  last_status: item.last_status ? item.last_status.id : null,
});

const updateConversation = (state, item) => state.withMutations(mutable => {
  const list = mutable.get('items');
  const index = list.findIndex(x => x.get('id') === item.id);
  const newItem = conversationToMap(item);
  const wasUnread = index === -1 ? false : list.getIn([index, 'unread']);
  const isUnread = !!item.unread;

  if (index === -1) {
    mutable.set('items', list.unshift(newItem));
  } else {
    mutable.set('items', list.set(index, newItem));
  }

  // Keep the badge in sync when a conversation newly becomes unread.
  if (isUnread && !wasUnread) {
    mutable.update('unreadCount', count => clampCount(count + 1));
  } else if (!isUnread && wasUnread) {
    mutable.update('unreadCount', count => clampCount(count - 1));
  }
});

const expandNormalizedConversations = (state, conversations, next, isLoadingRecent) => {
  let items = ImmutableList(conversations.map(conversationToMap));

  return state.withMutations(mutable => {
    if (!items.isEmpty()) {
      mutable.update('items', list => {
        list = list.map(oldItem => {
          const newItemIndex = items.findIndex(x => x.get('id') === oldItem.get('id'));

          if (newItemIndex === -1) {
            return oldItem;
          }

          const newItem = items.get(newItemIndex);
          items = items.delete(newItemIndex);

          return newItem;
        });

        list = list.concat(items);

        return list.sortBy(x => x.get('last_status'), (a, b) => {
          if(a === null || b === null) {
            return -1;
          }

          return compareId(a, b) * -1;
        });
      });
    }

    if (!next && !isLoadingRecent) {
      mutable.set('hasMore', false);
    }

    mutable.set('isLoading', false);
  });
};

const filterConversations = (state, accountIds) => {
  return state.update('items', list => list.filterNot(item => item.get('accounts').some(accountId => accountIds.includes(accountId))));
};

export default function conversations(state = initialState, action) {
  switch (action.type) {
  case CONVERSATIONS_FETCH_REQUEST:
    return state.set('isLoading', true);
  case CONVERSATIONS_FETCH_FAIL:
    return state.set('isLoading', false);
  case CONVERSATIONS_FETCH_SUCCESS:
    return expandNormalizedConversations(state, action.conversations, action.next, action.isLoadingRecent);
  case CONVERSATIONS_UPDATE:
    return updateConversation(state, action.conversation);
  case CONVERSATIONS_MOUNT:
    return state.update('mounted', count => count + 1);
  case CONVERSATIONS_UNMOUNT:
    return state.update('mounted', count => count - 1);
  case CONVERSATIONS_UNREAD_COUNT:
    return state.set('unreadCount', clampCount(action.count));
  case CONVERSATIONS_READ:
    return state.withMutations(mutable => {
      let decremented = false;

      mutable.update('items', list => list.map(item => {
        if (item.get('id') === action.id) {
          if (item.get('unread')) {
            decremented = true;
          }

          return item.set('unread', false);
        }

        return item;
      }));

      if (decremented) {
        mutable.update('unreadCount', count => clampCount(count - 1));
      }
    });
  case blockAccountSuccess.type:
  case muteAccountSuccess.type:
    return filterConversations(state, [action.payload.relationship.id]);
  case blockDomainSuccess.type:
    return filterConversations(state, action.payload.accounts);
  case CONVERSATIONS_DELETE_SUCCESS:
    return state.withMutations(mutable => {
      const deleted = mutable.get('items').find(item => item.get('id') === action.id);

      if (deleted && deleted.get('unread')) {
        mutable.update('unreadCount', count => clampCount(count - 1));
      }

      mutable.update('items', list => list.filterNot(item => item.get('id') === action.id));
    });
  default:
    return state;
  }
}
